import React, { useState, useRef, useEffect } from "react";
import {
  Building2,
  Plus,
  X,
  Upload,
  Image as ImageIcon,
  Trash2,
  Check,
} from "lucide-react";
import { toast } from "react-toastify";

/**
 * Sponsors Section Component
 * Dynamic list where users can add sponsors with name and logo
 */
const SponsorsSection = ({
  value = [],
  onChange,
  maxSize = 2,
  acceptedFormats = ["image/jpeg", "image/jpg", "image/png", "image/webp"],
  className = "",
}) => {
  const [sponsors, setSponsors] = useState(value || []);
  const [newSponsorName, setNewSponsorName] = useState("");
  const [newSponsorLogo, setNewSponsorLogo] = useState(null);
  const fileInputRef = useRef(null);

  // Update sponsors when value prop changes
  useEffect(() => {
    if (value && Array.isArray(value)) {
      setSponsors(value);
    }
  }, [value]);

  // File to Base64 converter
  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  // Validate file
  const validateFile = (file) => {
    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file");
      return false;
    }

    if (!acceptedFormats.includes(file.type)) {
      toast.error(
        `Accepted formats: ${acceptedFormats
          .map((f) => f.split("/")[1].toUpperCase())
          .join(", ")}`
      );
      return false;
    }

    if (file.size > maxSize * 1024 * 1024) {
      toast.error(`Image size must be less than ${maxSize}MB`);
      return false;
    }

    return true;
  };

  // Handle logo selection
  const handleLogoSelect = async (file) => {
    if (!file) return;

    if (!validateFile(file)) return;

    try {
      const base64 = await convertToBase64(file);
      setNewSponsorLogo(base64);
      toast.success("Logo uploaded");
    } catch (error) {
      toast.error("Failed to upload logo");
    }
  };

  // Handle file input change
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    handleLogoSelect(file);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Handle add sponsor
  const handleAddSponsor = () => {
    const trimmedName = newSponsorName.trim();

    if (!trimmedName) {
      toast.error("Please enter a sponsor name");
      return;
    }

    if (!newSponsorLogo) {
      toast.error("Please upload a sponsor logo");
      return;
    }

    if (sponsors.some((s) => s.name === trimmedName)) {
      toast.warning("This sponsor is already in the list");
      return;
    }

    const newSponsor = {
      name: trimmedName,
      logo: newSponsorLogo,
    };

    const updatedSponsors = [...sponsors, newSponsor];
    setSponsors(updatedSponsors);
    onChange(updatedSponsors);
    setNewSponsorName("");
    setNewSponsorLogo(null);
    toast.success("Sponsor added");
  };

  // Handle remove sponsor
  const handleRemoveSponsor = (index) => {
    const updatedSponsors = sponsors.filter((_, i) => i !== index);
    setSponsors(updatedSponsors);
    onChange(updatedSponsors);
    toast.success("Sponsor removed");
  };

  // Handle update sponsor logo
  const handleUpdateLogo = (index, file) => {
    if (!file) return;

    if (!validateFile(file)) return;

    convertToBase64(file)
      .then((base64) => {
        const updatedSponsors = [...sponsors];
        updatedSponsors[index].logo = base64;
        setSponsors(updatedSponsors);
        onChange(updatedSponsors);
        toast.success("Logo updated");
      })
      .catch(() => {
        toast.error("Failed to update logo");
      });
  };

  return (
    <div className={className}>
      <div className="p-4 bg-white rounded-xl border border-gray-100 shadow-sm sm:rounded-2xl">
        <div className="flex items-center mb-4 space-x-2">
          <Building2 className="w-5 h-5 text-purple-600" />
          <h3 className="text-lg font-bold text-gray-900">Sponsors</h3>
          <span className="px-2 py-0.5 text-xs font-medium text-gray-600 bg-gray-100 rounded-full">
            Optional
          </span>
          <span className="px-2 py-0.5 text-xs font-semibold text-purple-700 bg-purple-100 rounded-full">
            {sponsors.length}
          </span>
        </div>

        <p className="mb-4 text-xs text-gray-600">
          You can add sponsors for your event or skip this section entirely. If adding,
          include name and logo for each sponsor.
        </p>

        {/* Add Sponsor Form */}
        <div className="p-4 mb-4 bg-gray-50 rounded-lg border border-gray-200">
          <div className="space-y-3">
            {/* Sponsor Name Input */}
            <div>
              <label className="block mb-1.5 text-xs font-semibold text-gray-700">
                Sponsor Name
              </label>
              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 w-4 h-4 text-gray-400 -translate-y-1/2" />
                <input
                  type="text"
                  value={newSponsorName}
                  onChange={(e) => setNewSponsorName(e.target.value)}
                  placeholder="Enter sponsor name..."
                  className="w-full pl-10 pr-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all bg-white"
                />
              </div>
            </div>

            {/* Logo Upload */}
            <div>
              <label className="block mb-1.5 text-xs font-semibold text-gray-700">
                Sponsor Logo <span className="text-red-500">*</span>
              </label>
              {newSponsorLogo ? (
                <div className="relative inline-block">
                  <div className="overflow-hidden w-24 h-24 bg-gray-100 rounded-lg border-2 border-gray-200">
                    <img
                      src={newSponsorLogo}
                      alt="Sponsor logo preview"
                      className="object-contain w-full h-full p-2"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setNewSponsorLogo(null);
                      fileInputRef.current?.click();
                    }}
                    className="absolute -top-2 -right-2 p-1 text-white bg-blue-600 rounded-full shadow-lg transition-colors hover:bg-blue-700"
                    title="Change logo"
                  >
                    <ImageIcon className="w-3 h-3" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setNewSponsorLogo(null)}
                    className="absolute -bottom-2 -right-2 p-1 text-white bg-red-600 rounded-full shadow-lg transition-colors hover:bg-red-700"
                    title="Remove logo"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="flex items-center justify-center space-x-2 px-4 py-2.5 w-full text-sm font-medium text-gray-700 bg-white border-2 border-dashed border-gray-300 rounded-lg transition-colors hover:border-purple-400 hover:bg-purple-50"
                >
                  <Upload className="w-4 h-4" />
                  <span>Upload Logo</span>
                </button>
              )}
            </div>

            {/* Add Button */}
            <button
              type="button"
              onClick={handleAddSponsor}
              disabled={!newSponsorName.trim() || !newSponsorLogo}
              className="flex items-center justify-center space-x-2 px-4 py-2.5 w-full text-sm font-semibold text-white bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 rounded-lg transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Plus className="w-4 h-4" />
              <span>Add Sponsor</span>
            </button>
          </div>

          {/* Hidden File Input */}
          <input
            ref={fileInputRef}
            type="file"
            accept={acceptedFormats.join(",")}
            onChange={handleFileChange}
            className="hidden"
          />
        </div>

        {/* Sponsors List */}
        {sponsors.length > 0 ? (
          <div className="space-y-3">
            <p className="text-xs font-medium text-gray-700">
              Added Sponsors ({sponsors.length}):
            </p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {sponsors.map((sponsor, index) => (
                <div
                  key={index}
                  className="flex items-center space-x-3 p-3 bg-purple-50 border border-purple-200 rounded-lg group hover:bg-purple-100 transition-colors"
                >
                  {/* Logo */}
                  <div className="flex-shrink-0">
                    {sponsor.logo ? (
                      <div className="relative">
                        <div className="overflow-hidden w-16 h-16 bg-white rounded-lg border-2 border-purple-300">
                          <img
                            src={sponsor.logo}
                            alt={sponsor.name}
                            className="object-contain w-full h-full p-2"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            const input = document.createElement("input");
                            input.type = "file";
                            input.accept = acceptedFormats.join(",");
                            input.onchange = (e) => {
                              const file = e.target.files[0];
                              if (file) handleUpdateLogo(index, file);
                            };
                            input.click();
                          }}
                          className="absolute -top-1 -right-1 p-1 text-white bg-blue-600 rounded-full shadow opacity-0 transition-opacity group-hover:opacity-100"
                          title="Change logo"
                        >
                          <ImageIcon className="w-3 h-3" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center w-16 h-16 bg-white rounded-lg border-2 border-gray-300">
                        <Building2 className="w-6 h-6 text-gray-400" />
                      </div>
                    )}
                  </div>

                  {/* Name */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">
                      {sponsor.name}
                    </p>
                    {sponsor.logo && (
                      <p className="text-xs text-green-600 flex items-center mt-1">
                        <Check className="w-3 h-3 mr-1" />
                        Logo uploaded
                      </p>
                    )}
                  </div>

                  {/* Remove Button */}
                  <button
                    type="button"
                    onClick={() => handleRemoveSponsor(index)}
                    className="flex-shrink-0 p-2 text-red-600 bg-white rounded-lg border border-red-200 transition-colors hover:bg-red-50 hover:border-red-300"
                    title="Remove sponsor"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="py-8 text-center bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
            <Building2 className="w-12 h-12 mx-auto mb-3 text-gray-400" />
            <p className="text-sm font-medium text-gray-600">
              No sponsors added yet
            </p>
            <p className="mt-1 text-xs text-gray-500">
              Start adding sponsors using the form above
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SponsorsSection;

